import CompaniesDetails from "@/components/UserManagementComponents/Dialogs/DialogViewUser/companyDetails.component";
import UserDetails from "@/components/UserManagementComponents/Dialogs/DialogViewUser/userDetails.component";
import {
  fetchUserDetails,
  sendUpdateUserDetails,
} from "@/models/socket/admin.model";
import { Alert, AlertDescription } from "@/shadcn/components/ui/alert";
import { Button } from "@/shadcn/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/shadcn/components/ui/dialog";
import { UserAdminUpdateProfileDetails } from "@/types";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import React, { useEffect, useState } from "react";

type ViewUserDialogProps = {
  userID: string;
  onClose: () => void;
  updateUser: () => void;
};

export type ValidationErrors = {
  email?: string;
  title?: string;
  fullName?: string;
  designation?: string;
  phone_number?: string;
  preferredLanguage?: string;
  preferredCommunicationMethods?: string;
  marketingOptIn?: string;
  companies?: string[];
};

const ViewUserDialog: React.FC<ViewUserDialogProps> = ({
  userID,
  onClose,
  updateUser,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userDetails, setUserDetails] =
    useState<UserAdminUpdateProfileDetails | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [errors, setErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    async function fetchUserData() {
      setLoading(true);
      setError(null);
      setUserDetails(null);

      const response = await fetchUserDetails(userID);
      console.log("User details response:", response);
      if (response.rStatus === "success") {
        setUserDetails(response.rData);
      } else {
        setError(response.rData);
        console.error("Failed to fetch user data:", response.rData);
        genericFailureToast(`${response.rData}`);
      }
      setLoading(false);
    }

    fetchUserData();
  }, [userID]);

  async function submitChanges() {
    if (!userDetails) {
      console.error("No user details to submit.");
      return;
    }

    const valid = validateForm();
    if (!valid) {
      console.error("Form validation failed:", errors);
      genericFailureToast("Please fix the errors before submitting.");
      return;
    }

    const response = await sendUpdateUserDetails(userDetails);

    if (response.rStatus === "error") {
      console.error("Failed to update user details:", response.rData);
      genericFailureToast(`Failed to update user details: ${response.rData}`);
    } else {
      genericSuccessToast("User details updated successfully.");
      updateUser();
    }
    setSubmitting(false);
    onClose();
  }

  function validateForm(): boolean {
    // Using a new errors object to collect validation failures for form fields
    const newErrors: ValidationErrors = {};
    if (!userDetails) {
      console.error("No user details to validate.");
      return false;
    }

    // Email: must be present and valid format, length <= 255
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userDetails.email || !userDetails.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !emailRegex.test(userDetails.email) ||
      userDetails.email.length > 255
    ) {
      newErrors.email = "Invalid email address (max 255 chars)";
    }

    // Title: if present, must be from allowed options
    const TITLE_OPTIONS = ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."];
    if (userDetails.title && !TITLE_OPTIONS.includes(userDetails.title)) {
      newErrors.title = "Invalid title selected";
    }

    // Full Name: required, max 100, alphabetic and spaces only
    if (!userDetails.fullName || !userDetails.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (
      !/^([A-Za-z\s])+$/.test(userDetails.fullName) ||
      userDetails.fullName.length > 100
    ) {
      newErrors.fullName =
        "Full name must be alphabetic and up to 100 characters";
    }

    // Designation: optional, max 50
    if (userDetails.designation && userDetails.designation.length > 50) {
      newErrors.designation = "Designation must be 50 characters or less";
    }

    // Phone: required, valid, max 15
    const phoneRegex = /^\+?[0-9\s]{9,15}$/;
    if (!userDetails.phone_number || !userDetails.phone_number.trim()) {
      newErrors.phone_number = "Phone is required";
    } else if (
      !phoneRegex.test(userDetails.phone_number) ||
      userDetails.phone_number.length > 15
    ) {
      newErrors.phone_number = "Invalid phone number (max 15 chars)";
    }

    // Preferred Language: required, must be a valid code (assume 'en', 'af', etc.)
    if (
      !userDetails.preferredLanguage ||
      userDetails.preferredLanguage.length > 10
    ) {
      newErrors.preferredLanguage = "Preferred language is required";
    }

    // Preferred Communication Methods: at least one, must be 'email' or 'sms'
    if (
      !userDetails.preferredCommunicationMethods ||
      userDetails.preferredCommunicationMethods.length === 0
    ) {
      newErrors.preferredCommunicationMethods =
        "At least one communication method is required";
    } else if (
      !userDetails.preferredCommunicationMethods.every(
        (m) => m === "email" || m === "sms"
      )
    ) {
      newErrors.preferredCommunicationMethods = "Invalid communication method";
    }

    // Companies: must exist, each must have valid fields and at least one branch with valid address
    if (!userDetails.companies || userDetails.companies.length === 0) {
      newErrors.companies = ["At least one company is required"];
    } else {
      const companyErrors: string[] = [];
      userDetails.companies.forEach((company, idx) => {
        // Company Name: required for business, max 150
        if (
          company.company_type === "business" &&
          (!company.company_name || company.company_name.length > 150)
        ) {
          companyErrors.push(`Name required (max 150 chars)`);
        }
        // Account Number: optional, max 255
        if (company.account_number && company.account_number.length > 255) {
          companyErrors.push(`Account number max 255 chars`);
        }
        // Registration No: optional, max 50
        if (
          company.company_type === "business" &&
          company.registration_number &&
          company.registration_number.length > 50
        ) {
          companyErrors.push(`Registration number max 50 chars`);
        }
        // VAT: optional, max 20, must match format
        if (
          company.company_type === "business" &&
          company.tax_number &&
          (!/^([A-Za-z0-9-]{1,20})$/.test(company.tax_number) ||
            company.tax_number.length > 20)
        ) {
          companyErrors.push(`Invalid VAT number (max 20 chars)`);
        }
        // Industry Type: required for business
        if (
          company.company_type === "business" &&
          (!company.industry_type || company.industry_type.length > 100)
        ) {
          companyErrors.push(`Industry type required`);
        }
        // Company Contact Number: required, valid, max 15
        if (
          company.company_type === "business" &&
          (!company.phone_number ||
            !phoneRegex.test(company.phone_number) ||
            company.phone_number.length > 15)
        ) {
          companyErrors.push(`Valid contact number required (max 15 chars)`);
        }
        // Branches: at least one, all required address fields
        if (!company.branches || company.branches.length === 0) {
          companyErrors.push(`At least one branch required`);
        } else {
          company.branches.forEach((branch, bidx) => {
            // Address1: required, max 255
            if (
              !branch.branch_address1 ||
              branch.branch_address1.length > 255
            ) {
              companyErrors.push(`Address 1 required (max 255 chars)`);
            }
            // Address2: optional, max 255
            if (branch.branch_address2 && branch.branch_address2.length > 255) {
              companyErrors.push(`Address 2 max 255 chars`);
            }
            // Office Park Name: optional, max 150
            if (
              branch.branch_office_park_name &&
              branch.branch_office_park_name.length > 150
            ) {
              companyErrors.push(`Office Park Name max 150 chars`);
            }
            // Suburb: required, max 150
            if (!branch.branch_suburb || branch.branch_suburb.length > 150) {
              companyErrors.push(`Suburb required (max 150 chars)`);
            }
            // City: required, max 100
            if (!branch.branch_city || branch.branch_city.length > 100) {
              companyErrors.push(`City required, (max 100 chars)`);
            }
            // Province: required, max 100
            if (
              !branch.branch_province ||
              branch.branch_province.length > 100
            ) {
              companyErrors.push(`Province required, (max 100 chars)`);
            }
            // Postal Code: required, max 10, valid format
            if (
              !branch.branch_pin_code ||
              !/^([A-Za-z0-9\- ]){1,10}$/.test(branch.branch_pin_code) ||
              branch.branch_pin_code.length > 10
            ) {
              companyErrors.push(`Postal code required, valid (max 10 chars)`);
            }
            // Country: required, max 100
            if (!branch.branch_country || branch.branch_country.length > 100) {
              companyErrors.push(`Country required`);
            }
          });
        }
      });
      if (companyErrors.length > 0) {
        newErrors.companies = companyErrors;
      }
    }

    setErrors(newErrors);

    // Returning true only if no errors were found, to allow form submission

    if (Object.keys(newErrors).length > 0) {
      console.error("Validation errors found:", newErrors);
      return false;
    }

    return true;
  }

  return (
    <Dialog
      modal={true}
      open={!!userID}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="!h-auto !max-h-full w-full !max-w-full overflow-y-auto p-0 lg:!w-2xl">
        <div className="flex items-center justify-center">
          <div className="w-full max-w-3xl p-6">
            <DialogTitle className="mb-4 text-xl font-semibold">
              User Details
            </DialogTitle>
            {loading ? (
              <div className="h-svh py-4">
                <div className="flex items-center justify-center">
                  <div className="h-10 w-10 animate-spin rounded-full border-b-3 border-white"></div>
                </div>
                <div className="flex items-center justify-center">
                  <h2 className="mt-2 text-white">Loading User's Details...</h2>
                </div>
              </div>
            ) : error ? (
              <p className="text-red-500">{error}</p>
            ) : !userDetails ? (
              <p>No user details available.</p>
            ) : (
              <div>
                <UserDetails
                  userDetails={userDetails}
                  setUserDetails={setUserDetails}
                  errors={errors}
                />
                <CompaniesDetails
                  userDetails={userDetails}
                  setUserDetails={setUserDetails}
                  errors={errors}
                />
                <div className="mt-4">
                  {errors && Object.keys(errors).length > 0 && (
                    <Alert variant="destructive">
                      <AlertDescription className="!text-red-500 opacity-95">
                        Please review errors/requirements in the form
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
                <div className="mt-8 flex justify-between">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className=""
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    // variant="primary"
                    onClick={submitChanges}
                    className=""
                    disabled={submitting}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewUserDialog;
